import { Component, ErrorHandler, OnDestroy, ChangeDetectionStrategy } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { ActivatedRoute, Router } from '@angular/router'

import { Subject } from 'rxjs'

import { MatSnackBar } from '@angular/material/snack-bar'
import { ActionAfterAuthenticationLemonaid, NetsTunnistauduRequest, NetsTunnistauduResponse, NetsTunnistauduResponseResultCause } from '@lemon/core-firebase/model/tunnistaminen'
import { AllowedDialogsService } from 'app/_angular/service/allowed-dialogs.service'
import { FirebaseLemonaid } from 'app/_angular/service/firebase-lemonaid.service'
import { KayttajaService } from 'app/_angular/service/kayttaja.service'
import { TunnistautuminenService } from 'app/_angular/service/tunnistautuminen.service'
import { LemonTranslationService } from 'app/_jaettu-angular/service/lemon-translation.service'
import { VaihdaKieliDialog, VaihdaKieliDialogData } from 'app/vaihda-kieli.dialog'
import { environment } from 'environments/environment'
import { LadataanService } from '../../_jaettu-angular/service/ladataan.service'

@Component({
  templateUrl: './nets-return.component.html',
  styleUrls: ['./nets-return.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TunnistautuminenNetsReturnComponent implements OnDestroy {
  private ngUnsubscribe: Subject<void> = new Subject<void>()

  virhe: string = null
  year = new Date().getFullYear()

  constructor(
    private _activatedRoute: ActivatedRoute,
    private _errorHandler: ErrorHandler,
    private _dialog: MatDialog,
    private _snackbar: MatSnackBar,
    private _ladataanService: LadataanService,
    private _router: Router,
    private _tunnistautuminenService: TunnistautuminenService,
    private _allowedDialogsService: AllowedDialogsService,
    private _kayttajaService: KayttajaService,
    private _firebaseLemonaid: FirebaseLemonaid,
    private _translationService: LemonTranslationService,
  ) {
    this._allowedDialogsService.disableAllDialogs()
    this._ladataanService.aloitaLataaminen()

    const errorCode = this._activatedRoute.snapshot.queryParamMap.get('error')
    const errorDescription = this._activatedRoute.snapshot.queryParamMap.get('error_description')
    const errorURIString = this._activatedRoute.snapshot.queryParamMap.get('error_uri')
    const code = this._activatedRoute.snapshot.queryParamMap.get('code')
    const stateHash = this._activatedRoute.snapshot.queryParamMap.get('state')

    const clientSession = this._tunnistautuminenService.getClientSideAuthenticationSession()

    if (!clientSession) {
      this._errorHandler.handleError(new Error('Tunnistautumissession dataa ei löytynyt Session Storagesta!'))
    }

    this._kayttajaService
      .getKayttaja()
      .then(kayttaja => {
        const netsEnv = this._tunnistautuminenService.getNetsEnvironment(environment.environment)
        return this._tunnistautuminenService.saveHistory(kayttaja, 'returned', 'Includes code: ' + !!code + (errorCode ? 'Error: ' + errorCode : ''), netsEnv)
      })
      .catch(err => {
        this._errorHandler.handleError(new Error('Strong auth log 1 save failed ' + err?.message || ''))
      })

    if (errorCode || errorDescription || errorURIString) {
      this._ladataanService.lopetaLataaminen()
      this._errorHandler.handleError(new Error('Error in strong auth return component: ' + errorCode + ' ,__, ' + errorDescription + ' ,__, ' + errorURIString))
      this._allowedDialogsService.onlyAllowTervetuloaOrTunnistaminen()
      this._tunnistautuminenService.setTunnistautuminenState(code === 'cancel' ? 'error-cancel' : 'error')
      this._tunnistautuminenService.navigateByAction(clientSession?.action ?? 'continue-welcome')
    } else if (clientSession && code && stateHash) {
      const pyynto: NetsTunnistauduRequest = {
        avain: clientSession.sessionAvain,
        code: code,
        state: clientSession.state,
        stateHash: stateHash,
        env: this._tunnistautuminenService.getNetsEnvironment(environment.environment),
      }
      const func = this._getFunctionByAction(clientSession.action)
      this._firebaseLemonaid
        .functionsCall<NetsTunnistauduRequest, NetsTunnistauduResponse>(func, pyynto)
        .then(vastaus => {
          if (vastaus.result === 'success') {
            this._ladataanService.lopetaLataaminen()
            this._tunnistautuminenService.setTunnistautuminenState('success')
            this._allowedDialogsService.onlyAllowTervetuloaOrTunnistaminen()
            this._tunnistautuminenService.navigateByAction(clientSession.action ?? 'continue-welcome', vastaus.data)
          } else {
            this._ladataanService.lopetaLataaminen()
            this._errorHandler.handleError('Backend error while checking NETS authentication.')
            if (vastaus.resultCause === NetsTunnistauduResponseResultCause.SSN_MISMATCH) {
              const translationKey = this._getSsnMismatchTranslationKey(clientSession.action)
              if (translationKey) {
                this._snackbar.open(this._translationService.lokalisoi(translationKey), 'OK', { duration: 10 * 1000, verticalPosition: 'top' })
              }
            }
            this._allowedDialogsService.onlyAllowTervetuloaOrTunnistaminen()
            this._tunnistautuminenService.setTunnistautuminenState('error')
            this._tunnistautuminenService.navigateByAction(clientSession.action ?? 'continue-welcome')
          }
        })
        .catch(err => {
          this._ladataanService.lopetaLataaminen()
          this._errorHandler.handleError(err)
          this._allowedDialogsService.onlyAllowTervetuloaOrTunnistaminen()
          this._tunnistautuminenService.setTunnistautuminenState('error')
          this._tunnistautuminenService.navigateByAction(clientSession.action ?? 'continue-welcome')
        })
    } else {
      this._ladataanService.lopetaLataaminen()
      this._errorHandler.handleError(
        new Error('Error in strong auth return component: Missing client session, code or state. Client session: ' + clientSession?.sessionAvain + ', Code: ' + code + ', state: ' + stateHash),
      )
      this._allowedDialogsService.onlyAllowTervetuloaOrTunnistaminen()
      this._tunnistautuminenService.setTunnistautuminenState('error')
      this._tunnistautuminenService.navigateByAction(clientSession?.action ?? 'continue-welcome')
    }
  }

  private _getFunctionByAction(action: ActionAfterAuthenticationLemonaid): string {
    if (action === 'continue-payment-approval') {
      return 'tunnistautuminenNetsTarkistaMaksut'
    }
    if (action === 'continue-aineiston-lataus') {
      return 'tunnistautuminenNetsTarkistaAineistonLataus'
    }
    return 'tunnistautuminenNetsTarkista'
  }

  private _getSsnMismatchTranslationKey(action: ActionAfterAuthenticationLemonaid): string | null {
    const record: Record<ActionAfterAuthenticationLemonaid, string> = {
      'continue-welcome': null, // Ei käytössä
      'continue-standalone-dialog': null, // Ei käytössä
      'continue-document-sign': null, // Ei käytössä
      'continue-payment-approval': 'maksut.vahvistaminen-epaonnistui-ssn',
      'continue-aineiston-lataus': 'aineiston-lataus.vahvistaminen-epaonnistui-ssn',
    }
    return record[action]
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next()
    this.ngUnsubscribe.complete()
  }

  vaihdaKieli() {
    const data: VaihdaKieliDialogData = {
      tallenna: false,
    }
    this._dialog.open(VaihdaKieliDialog, { data: data })
  }
}
